import { createSlice } from "@reduxjs/toolkit";
import { LOADING_STATE } from "types/general";
import { PORTFOLIO_SLICE_NAME, initialState } from "./constants";
import {
  fetchPortfolioDashboard,
  fetchPortfolioSelection,
  savePortfolioSelection,
} from "./controllers";
import { normalizePortfolioSelection } from "./normalizePortfolioSelection";

const portfolioSlice = createSlice({
  name: PORTFOLIO_SLICE_NAME,
  initialState,
  reducers: {
    resetPortfolioSelection: (state) => {
      state.selection = initialState.selection;
    },
    toggleSubjectSelected: (
      state,
      { payload }: { payload: { subjectId: string } }
    ) => {
      const deals = state.selection.data?.deals ?? [];
      const matches = deals.flatMap((deal) =>
        deal.subjects.filter((subject) => subject.id === payload.subjectId)
      );
      const nextSelected = !matches[0]?.selected;

      matches.forEach((subject) => {
        subject.selected = nextSelected;
      });
    },
    setDealSubjectsSelected: (
      state,
      { payload }: { payload: { dealId: string; selected: boolean } }
    ) => {
      const deals = state.selection.data?.deals ?? [];
      const targetDeal = deals.find((deal) => deal.id === payload.dealId);
      const subjectIds = new Set(
        targetDeal?.subjects.map((subject) => subject.id)
      );

      deals.forEach((deal) => {
        deal.subjects.forEach((subject) => {
          if (subjectIds.has(subject.id)) {
            subject.selected = payload.selected;
          }
        });
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolioSelection.pending, ({ selection }) => {
        selection.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchPortfolioSelection.rejected, ({ selection }) => {
        selection.loadingState = LOADING_STATE.rejected;
      })
      .addCase(
        fetchPortfolioSelection.fulfilled,
        ({ selection }, { payload }) => {
          selection.data = normalizePortfolioSelection(payload);
          selection.loadingState = LOADING_STATE.fulfilled;
        }
      )
      .addCase(
        savePortfolioSelection.fulfilled,
        ({ selection }, { payload }) => {
          selection.data = normalizePortfolioSelection(payload);
        }
      )
      .addCase(fetchPortfolioDashboard.pending, ({ dashboard }) => {
        dashboard.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchPortfolioDashboard.rejected, ({ dashboard }) => {
        dashboard.loadingState = LOADING_STATE.rejected;
      })
      .addCase(
        fetchPortfolioDashboard.fulfilled,
        ({ dashboard }, { payload }) => {
          dashboard.data = payload;
          dashboard.loadingState = LOADING_STATE.fulfilled;
        }
      );
  },
});

export const {
  resetPortfolioSelection,
  toggleSubjectSelected,
  setDealSubjectsSelected,
} = portfolioSlice.actions;

export default portfolioSlice;
